import {
  assetTaggingSchema,
  setAssetTagsRequestSchema,
  setAssetTagsResultSchema,
  type AssetTagging,
  type SetAssetTagsResult,
} from '@xphoto/shared';
import { ApiError } from '../gallery/api';
import { apiFetch } from '../lib/http';

/**
 * Client typé du nouveau système « Taguer » (refonte de l'ancien « Valider les tags IA »).
 *
 * Chaque réponse est RE-PARSÉE via son schéma partagé `@xphoto/shared` (« never trust the
 * client », y compris ses propres réponses). Les erreurs sont propagées en `ApiError`
 * (statut + code) — le statut permet de discriminer un 403 (accès lecture seule).
 */

async function readJson(res: Response): Promise<unknown> {
  if (!res.ok) {
    let code = 'UNKNOWN';
    try {
      const body = (await res.json()) as { error?: { code?: string } };
      if (typeof body?.error?.code === 'string') code = body.error.code;
    } catch {
      // Corps non-JSON : on garde le code générique.
    }
    throw new ApiError(res.status, code);
  }
  return res.json();
}

/**
 * `GET /api/assets/:id/tagging` — le drapeau `tagged` de la photo + TOUTE la taxonomie
 * (ordre alphabétique) fusionnée avec ses liaisons (coché / confiance IA). Le panneau
 * de tagging peint une case par tag.
 */
export async function fetchAssetTagging(
  assetId: string,
  signal?: AbortSignal,
): Promise<AssetTagging> {
  const res = await apiFetch(`/api/assets/${assetId}/tagging`, { signal: signal ?? null });
  return assetTaggingSchema.parse(await readJson(res));
}

/**
 * `POST /api/assets/:id/tags/set` — REMPLACE l'ensemble des tags validés de la photo par
 * le set EXACT fourni (les tags absents sont décochés). Une liste VIDE = commit à zéro tag
 * (la photo est marquée `tagged` sans porter de tag). 403 si accès lecture seule.
 */
export async function setAssetTags(assetId: string, tagIds: string[]): Promise<SetAssetTagsResult> {
  const body = setAssetTagsRequestSchema.parse({ tagIds });
  const res = await apiFetch(`/api/assets/${assetId}/tags/set`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return setAssetTagsResultSchema.parse(await readJson(res));
}
